import { useNavigate } from 'react-router-dom'

import { PaginatedResponse, RecordType } from '@factorialco/f0-react'
import { OneDataCollection, useDataCollectionSource } from '@factorialco/f0-react/dist/experimental'
import { ExternalLink } from '@factorialco/f0-react/icons/app'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

interface EmptyCostCentersProps {
  hasConfigurationPending: boolean
  canManageCostCenters: boolean
  hasWorkplaces: boolean
  hasJobCatalog: boolean
  startConfiguration: () => void
}

// We use DataCollection here to check if any cost centers exist before rendering
// the employee assignments list. This ensures consistent styling and automatic
// updates when DataCollection styles change. The empty state redirects users to
// create their first cost center, while the main collection displays employees
// with their cost center assignments (current and future).
export const EmptyCostCentersStructure = ({
  hasConfigurationPending,
  canManageCostCenters,
  hasWorkplaces,
  hasJobCatalog,
  startConfiguration,
}: EmptyCostCentersProps) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const noDataTitle = useUnsafeMemo(() => {
    if (hasConfigurationPending) return i18n.t('finance.cost_centers_structure.empty.all_set')

    return i18n.t('finance.cost_centers_structure.empty.complete')
  }, [hasConfigurationPending])

  const noDataDescription = useUnsafeMemo(() => {
    if (hasConfigurationPending) return i18n.t('finance.cost_centers_structure.empty.pending')

    if (!canManageCostCenters) return ''

    if (!hasWorkplaces && !hasJobCatalog) {
      return i18n.t('finance.cost_centers_structure.empty.no_wp_no_jc_description')
    }

    if (!hasWorkplaces) {
      return i18n.t('finance.cost_centers_structure.empty.no_wp_description')
    }

    if (!hasJobCatalog) {
      return i18n.t('finance.cost_centers_structure.empty.no_jc_description')
    }

    return i18n.t('finance.cost_centers_structure.empty.description')
  }, [hasWorkplaces, hasJobCatalog, canManageCostCenters, hasConfigurationPending])

  const noDataActions = useUnsafeMemo(() => {
    if (!canManageCostCenters) return []

    const actions = []

    if (!hasWorkplaces || hasConfigurationPending) {
      actions.push({
        icon: ExternalLink,
        variant: 'outline' as const,
        label: i18n.t('finance.cost_centers_structure.empty.go_to_workplaces'),
        onClick: () => {
          // Todo later
        },
      })
    }
    if (!hasJobCatalog || hasConfigurationPending) {
      actions.push({
        icon: ExternalLink,
        variant: 'outline' as const,
        label: i18n.t('finance.cost_centers_structure.empty.go_to_job_catalog'),
        onClick: () => {
          navigate(navigation.employees.jobCatalog.fullPath())
        },
      })
    } else if (hasJobCatalog) {
      actions.push({
        label: i18n.t('finance.cost_centers_structure.empty.start_with_job_catalog'),
        onClick: () => {
          startConfiguration()
        },
      })
    }

    return actions
  }, [
    hasWorkplaces,
    hasJobCatalog,
    startConfiguration,
    hasConfigurationPending,
    canManageCostCenters,
    navigate,
    navigation,
  ])

  const source = useDataCollectionSource({
    dataAdapter: {
      fetchData: (): Promise<PaginatedResponse<RecordType>> => {
        return Promise.resolve({
          type: 'pages',
          records: [],
          total: 0,
          currentPage: 1,
          perPage: 10,
          pagesCount: 1,
        })
      },
      perPage: 10,
      paginationType: 'pages',
    },
  })

  return (
    <OneDataCollection
      source={source}
      visualizations={[{ type: 'table', options: { columns: [] } }]}
      emptyStates={{
        'no-data': {
          title: noDataTitle,
          description: noDataDescription,
          actions: noDataActions,
        },
      }}
    />
  )
}
